import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Button, type ButtonStyles } from '@nexiahome/ui-common/components/primitives/buttons'

import Spinner from 'components/Spinner'

import useSearchParams from 'hooks/useSearchParams'
import useEmailUnsubscribeStatus from 'hooks/useEmailUnsubscribeStatus'
import useDealer from 'hooks/useDealer'

import { ButtonContainer, PageHeader, PageTitle, SinglePanePage } from 'styles/shared'
import { color } from 'styles/colors'
import Brand from 'helpers/brand'
import Api from 'services/api'

const UnsubscribeEmail = () => {
  const email = useSearchParams('email')
  const dealerId = useSearchParams('dealerId')
  const type = useSearchParams('type') ?? ''

  const { isUnsubscribed, areUnsubscribedLoaded, setIsUnsubscribed } = useEmailUnsubscribeStatus(email, type)
  const { dealer, isDealerLoaded } = useDealer(dealerId)

  const [isUnsubscribeLoading, setIsUnsubscribeLoading] = useState(false)

  const handleUnsubscribe = async () => {
    try {
      setIsUnsubscribeLoading(true)
      await Api.UnsubscribeEmails.create(
        email,
        type
      )
      setIsUnsubscribed(true)
    } catch (error) {
      console.error(error)
    } finally {
      setIsUnsubscribeLoading(false)
    }
  }

  if (!areUnsubscribedLoaded || !isDealerLoaded) { return <Spinner variant="page" /> }
  return (
    <S.Container>
      <S.Body>
        <S.PageHeader>
          <S.PageTitle>Subscription Preferences</S.PageTitle>
          <S.BrandLogo src={Brand.brandLogoPath()} alt={`${Brand.brandName()} logo`} />
        </S.PageHeader>
        <SinglePanePage>
          <S.CardHeader>
            <S.DealerLogo src={dealer?.imageUrl} alt={`${dealer?.name} logo`} />
          </S.CardHeader>
          <S.CardBody alignCenter={isUnsubscribed}>
            {
             isUnsubscribed ? (
               <S.UnsubscribeNote>
                 You have successfully opted out of receiving <b>{type?.replace('_', ' ')}</b> emails from your dealer.
               </S.UnsubscribeNote>
             )
               : (
                 <>
                   <S.Label>Email preferences for: <i>{email}</i></S.Label>
                   <S.LabelNote>Please confirm you would like to opt out of all <b>{type?.replace('_', ' ')}</b> emails.</S.LabelNote>
                   <S.Note>*This action cannot be undone.</S.Note>
                 </>

               )
          }
          </S.CardBody>
          {
          !isUnsubscribed && (
          <S.ButtonContainer noBorder>
            <Button decoration="positive" text="Unsubscribe" styles={buttonStyle} onPress={handleUnsubscribe} showSpinner={isUnsubscribeLoading} />
          </S.ButtonContainer>
          )
        }
        </SinglePanePage>
      </S.Body>
    </S.Container>
  )
}

const buttonStyle: ButtonStyles = {
  text: {
    textTransform: 'capitalize'
  }
}

export default UnsubscribeEmail

const S = {
  Container: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  Body: styled.div`
   height: 100%;
   width: 60%;
   margin: 0 auto;
 `,
  PageHeader: styled(PageHeader)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
 `,
  BrandLogo: styled.img`
    height: 36px;
  `,
  DealerLogo: styled.img`
    width: 81px;
    height: 70px;
  `,
  CardHeader: styled.div`
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    padding: 3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${color('emailBackground')};
  `,
  CardBody: styled.div.withConfig({
    shouldForwardProp: (prop) => !['alignCenter'].includes(prop)
  })<{ alignCenter: boolean }>`
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
    background-color: ${color('chartBackground')};
    padding: 3rem;
    display: flex;
    flex-direction: column;
    gap:1rem;
    min-height: 300px;
    ${({ alignCenter }) => (alignCenter && css`
      justify-content: center;
    `)}
  `,
  PageTitle: styled(PageTitle)`
    text-transform: capitalize;
    color: ${color('primaryText')};
  `,
  Label: styled.label`
    color: ${color('serviceContractNone')};
  `,
  LabelNote: styled.label`
 `,
  Note: styled.span`
    color: ${color('needsAttention')};
    display: block;
  `,
  ButtonContainer: styled(ButtonContainer)`
    padding-top: 0;
  `,
  UnsubscribeNote: styled.div`
    text-align: center;
    text-transform: none;
    font-size: 1rem;
  `
}
